import { Component } from '@theme/component';
import { buildSearchIntent, buildCandidateSearchQuery, normalizeSearchURL, filterPredictiveResults } from '@theme/fastutv-search-intent';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  #emptyStateSnapshot = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#emptyStateSnapshot ??= this.refs.predictiveSearchResults.cloneNode(true);
    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    this.#runSearch.cancel();
    this.resetSearch.cancel();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ ([...new Set(allItems)]);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    this.refs.searchInput.removeAttribute('aria-activedescendant');
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.id ||= `${this.refs.searchInput.id || 'predictive-search'}-option-${itemIndex}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'true');
        this.refs.searchInput.setAttribute('aria-activedescendant', item.id);
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    if (index >= 0) this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.isComposing || event.keyCode === 229) return;
    if (event.key !== 'Escape' && event.target !== this.refs.searchInput) return;
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        // Arrow keys choose suggestions; Tab retains the dialog's native focus order.
        this.#currentIndex = -1;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          const rawIntent=buildSearchIntent(this.refs.searchInput.value);
          const vehicle=document.documentElement.dataset.fastutvVehicle;
          if(vehicle&&!rawIntent.model&&!rawIntent.make&&!rawIntent.advanced)searchUrl.searchParams.set('fastutv_vehicle',vehicle);
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) searchUrl.searchParams.set('preview_theme_id', preview);
          window.location.href = normalizeSearchURL(searchUrl, this.refs.searchInput.value).href;
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = (event) => {
    if (event.isComposing) return;
    this.#activeFetch?.abort();
    this.#currentIndex = -1;
    this.refs.predictiveSearchResults.style.visibility = 'hidden';
    this.refs.searchInput.setAttribute('aria-expanded','false');
    this.#runSearch(event);
  };

  #runSearch = debounce((event) => {

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId || !this.isConnected) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    const rawIntent=buildSearchIntent(searchTerm);
    const vehicle=document.documentElement.dataset.fastutvVehicle;
    const intent=vehicle&&!rawIntent.model&&!rawIntent.make&&!rawIntent.advanced?buildSearchIntent(vehicle+' '+searchTerm):rawIntent;
    // Native residual membership can include body-only keyword matches. Keep
    // vehicle/category enforcement local without rechecking absent body metadata.
    const nativeResidual = intent.scoped && Boolean(intent.residual);
    const matchingIntent = nativeResidual ? { ...intent, residual: '' } : intent;
    url.searchParams.set('q', buildCandidateSearchQuery(intent));
    url.searchParams.set('resources[limit_scope]', 'each');
    if (intent.scoped) url.searchParams.set('resources[type]', 'product');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);

    const { predictiveSearchResults } = this.refs;
    predictiveSearchResults.style.visibility = 'hidden';
    predictiveSearchResults.setAttribute('aria-busy', 'true');

    const abortController = this.#createAbortController();

    // Each suggestion request belongs to this input. Shared section promises
    // cannot cancel obsolete typing and can retain a failed request for a retry.
    url.searchParams.set('section_id', this.dataset.sectionId.replace(/^shopify-section-/, ''));
    fetch(url, { signal: abortController.signal, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Search suggestions HTTP ' + response.status);
        return response.text();
      })
      .then((resultsMarkup) => {
        if (!resultsMarkup?.trim()) throw new Error('Empty predictive search response');

        if (abortController.signal.aborted || this.refs.searchInput.value.trim() !== searchTerm) return;
        if (!new DOMParser().parseFromString(resultsMarkup, 'text/html').querySelector('#predictive-search-results')) throw new Error('Search suggestions unavailable');
        morph(predictiveSearchResults, filterPredictiveResults(resultsMarkup, matchingIntent, { count: this.dataset.fastutvCountTemplate, empty: this.dataset.fastutvEmptyText }));
        predictiveSearchResults.style.visibility = '';
        predictiveSearchResults.removeAttribute('aria-busy');
        this.refs.searchInput.setAttribute('aria-expanded', String(this.#allResultsItems.length > 0));

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        this.refs.searchInput.setAttribute('aria-expanded', 'false');
        this.refs.searchInput.removeAttribute('aria-activedescendant');
        predictiveSearchResults.replaceChildren();
        predictiveSearchResults.style.visibility = '';
        predictiveSearchResults.removeAttribute('aria-busy');
        console.warn('Search suggestions unavailable', error);
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#runSearch.cancel();
    this.#currentIndex = -1;
    searchInput.value = '';
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.removeAttribute('aria-activedescendant');
    predictiveSearchResults.style.visibility = '';
    predictiveSearchResults.removeAttribute('aria-busy');
    // Restore the existing empty state immediately, including on network failure.
    if (this.#emptyStateSnapshot) morph(predictiveSearchResults, this.#emptyStateSnapshot.cloneNode(true));
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    try {
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    } catch (error) {
      if (!abortController.signal.aborted) {
        this.#emptyStateLoaded = false;
        console.warn('Empty search state unavailable', error);
      }
    } finally {
      if (this.#activeFetch === abortController) {
        predictiveSearchResults.style.visibility = '';
        predictiveSearchResults.removeAttribute('aria-busy');
      }
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
